import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { logger } from '../../datadog';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { toast } from '../../toasts';
import { ProviderDrawerState, drawerStateAtom, selectedClientIdAtom } from '../state';
import { useLazyGetPatientByShortId, useSetPatientDataCallback } from '../useGetPatientByShortId';
import { getIsClientInfoDrawerOpen } from '../utils';

export function useOpenClientInformationCallback() {
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setSelectedClientId = useSetAtom(selectedClientIdAtom);
  const setPatientData = useSetPatientDataCallback();
  const [fetchPatient] = useLazyGetPatientByShortId();

  return useCallback(
    async (
      {
        visitorUuid,
        patientShortId,
      }: {
        visitorUuid?: string;
        patientShortId: string;
      },
      drawerState: ProviderDrawerState = ProviderDrawerState.CLIENT_INFORMATION,
    ) => {
      if (!patientShortId) {
        logger.error(
          'Missing patient short ID when opening client information drawer (useClientInformationDrawerCallbacks)',
        );
        return;
      }
      // We prefer the visitor UUID to match the selected client to the
      // participant in the session or in the waiting room, but in the case of a
      // scheduled patient who may not currently be in the session, we use the
      // patient short ID instead.
      setSelectedClientId(visitorUuid ?? patientShortId);
      // We immediately set the drawer state to provide feedback to the user.
      // The drawer should handle its own loading state in the absence of
      // patient data.
      setDrawerState(drawerState);
      // Ensure we've fetched the data for this patient.
      const { data, error } = await fetchPatient({
        variables: { patientShortId },
      });
      if (error && !data) {
        logger.error('Unable to fetch patient information', { patientShortId }, error);
        toast.error('Unable to retrieve patient information');
      }
      // Run the onCompleted callback to populate the rest of the selected
      // client/patient data in Jotai state.
      if (data) setPatientData(data);
    },
    [fetchPatient, setDrawerState, setPatientData, setSelectedClientId],
  );
}

export function useCloseClientInformationCallback() {
  const setSelectedClientId = useSetAtom(selectedClientIdAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);

  return useCallback(() => {
    setDrawerState(null);
    setSelectedClientId(undefined);
  }, [setDrawerState, setSelectedClientId]);
}

export function useToggleClientInformationDrawerCallback() {
  const getDrawerState = useAtomCallbackMemoized((get) => get(drawerStateAtom), []);
  const openClientInformationDrawer = useOpenClientInformationCallback();
  const closeClientInformationDrawer = useCloseClientInformationCallback();

  return useCallback(
    (...openClientInformationDrawerCallbackArgs: Parameters<typeof openClientInformationDrawer>) => {
      const drawerState = getDrawerState();
      if (getIsClientInfoDrawerOpen(drawerState)) {
        closeClientInformationDrawer();
        return;
      }
      openClientInformationDrawer(...openClientInformationDrawerCallbackArgs);
    },
    [closeClientInformationDrawer, getDrawerState, openClientInformationDrawer],
  );
}

export function useClientInformationDrawerCallbacks() {
  return {
    openClientInformationDrawer: useOpenClientInformationCallback(),
    closeClientInformationDrawer: useCloseClientInformationCallback(),
    toggleClientInformationDrawer: useToggleClientInformationDrawerCallback(),
  };
}
